import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import BeforeAfterReport from '../components/BeforeAfterReport';
import { useLanguage } from '../context/LanguageContext';

const TrackComplaint = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const ticketFromUrl = searchParams.get('ticket') || '';

  const [ticketInput, setTicketInput] = useState(ticketFromUrl);
  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (ticketFromUrl) {
      handleTrack(ticketFromUrl);
    }
  }, [ticketFromUrl]);

  const handleTrack = async (ticketNum) => {
    const targetTicket = ticketNum || ticketInput;
    if (!targetTicket.trim()) {
      setError(t('trackSubtitle'));
      return;
    }

    setError('');
    setLoading(true);
    setComplaint(null);

    try {
      const res = await api.get(`/complaints/track/${encodeURIComponent(targetTicket.trim())}`);
      setComplaint(res.data);
    } catch (err) {
      console.error('Track Complaint Error:', err);
      setError(err.response?.data?.message || 'No complaint found matching this ticket number.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleTrack();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Submitted': return 'bg-info text-white';
      case 'Assigned': return 'bg-warning text-dark';
      case 'In Progress': return 'bg-primary text-white';
      case 'Pending Customer': return 'bg-secondary text-white';
      case 'Resolved': return 'bg-success text-white';
      case 'Closed': return 'bg-dark text-white';
      case 'Escalated': return 'bg-danger text-white';
      default: return 'bg-secondary text-white';
    }
  };

  return (
    <div className="container py-5">
      {/* Search Header */}
      <div className="row justify-content-center mb-5">
        <div className="col-lg-6 text-center">
          <span className="badge bg-primary fs-6 px-3 py-2 text-uppercase mb-2">{t('publicCitizenDesk')}</span>
          <h2 className="brand-font fw-bold text-dark">{t('trackComplaintStatus')}</h2>
          <p className="text-muted small">{t('trackSubtitle')}</p>

          <form onSubmit={handleSubmit} className="mt-4">
            <div className="input-group input-group-lg shadow-sm rounded-pill overflow-hidden">
              <input
                type="text"
                className="form-control border-0 px-4"
                placeholder={t('trackTicketPlaceholder')}
                value={ticketInput}
                onChange={(e) => setTicketInput(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary-gradient px-4 fw-bold" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : t('trackStatusBtn')}
              </button>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              <i className="bi bi-exclamation-octagon-fill me-2"></i>{error}
            </div>
          )}
        </div>
      </div>

      {/* Ticket Details View */}
      {complaint && (
        <div className="card glass-card border-0 p-4 shadow-lg rounded-4">
          <div className="border-bottom pb-3 mb-4 d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <span className="text-muted small text-uppercase d-block">{t('ticketRef')}</span>
              <h3 className="fw-bold text-primary mb-0">{complaint.ticket_number}</h3>
            </div>
            <div className="text-end">
              <span className={`badge fs-6 px-3 py-2 rounded-pill ${getStatusBadgeClass(complaint.status)}`}>
                {t(`status_${complaint.status.replace(/\s+/g, '')}`, complaint.status)}
              </span>
            </div>
          </div>

          {/* Visual Progress Timeline Tracker */}
          <div className="my-4 px-3">
            <div className="d-flex justify-content-between align-items-center position-relative">
              <div className="text-center z-1">
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold ${['Submitted', 'Assigned', 'In Progress', 'Resolved', 'Closed'].includes(complaint.status) ? 'bg-primary' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-file-earmark-check-fill"></i>
                </div>
                <small className="d-block fw-bold mt-1 text-dark">{t('status_Submitted')}</small>
              </div>
              <div className="text-center z-1">
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold ${['Assigned', 'In Progress', 'Resolved', 'Closed'].includes(complaint.status) ? 'bg-primary' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-person-check-fill"></i>
                </div>
                <small className="d-block fw-bold mt-1 text-dark">{t('status_Assigned')}</small>
              </div>
              <div className="text-center z-1">
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold ${['In Progress', 'Resolved', 'Closed'].includes(complaint.status) ? 'bg-primary' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-gear-wide-connected"></i>
                </div>
                <small className="d-block fw-bold mt-1 text-dark">{t('status_InProgress')}</small>
              </div>
              <div className="text-center z-1">
                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold ${['Resolved', 'Closed'].includes(complaint.status) ? 'bg-success' : 'bg-secondary'}`} style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <small className="d-block fw-bold mt-1 text-dark">{t('status_Resolved')}</small>
              </div>
            </div>
          </div>

          <h4 className="fw-bold mb-2 text-dark">{complaint.title}</h4>
          <p className="text-secondary bg-light p-3 rounded-3 border">{complaint.description}</p>

          <div className="row g-3 my-3">
            <div className="col-sm-6 col-md-3">
              <div className="p-3 border rounded-3 bg-white">
                <small className="text-muted d-block fw-semibold">{t('submittedBy')}</small>
                <strong>{complaint.customer_name}</strong>
              </div>
            </div>
            <div className="col-sm-6 col-md-3">
              <div className="p-3 border rounded-3 bg-white">
                <small className="text-muted d-block fw-semibold">{t('category')}</small>
                <span>{complaint.category}</span>
              </div>
            </div>
            <div className="col-sm-6 col-md-3">
              <div className="p-3 border rounded-3 bg-white">
                <small className="text-muted d-block fw-semibold">{t('tablePriority')}</small>
                <span className="badge bg-warning text-dark me-1">{t(`priority_${complaint.priority}`, complaint.priority)}</span>
              </div>
            </div>
            <div className="col-sm-6 col-md-3">
              <div className="p-3 border rounded-3 bg-white">
                <small className="text-muted d-block fw-semibold">{t('targetSLADue')}</small>
                <small className="fw-bold">{new Date(complaint.sla_due_at).toLocaleDateString()}</small>
              </div>
            </div>
          </div>

          {/* Side by side Before & After Visual Comparison Card */}
          <BeforeAfterReport complaint={complaint} isStaff={false} />

          {/* Area & Elected Officials Info */}
          {complaint.area_name && (
            <div className="card border-info bg-info-subtle mb-4">
              <div className="card-body">
                <h6 className="fw-bold text-info-emphasis mb-2">
                  <i className="bi bi-geo-alt-fill me-2"></i> {t('registeredWard')}: {complaint.area_name} ({complaint.area_code})
                </h6>
                {complaint.representatives && complaint.representatives.length > 0 ? (
                  <div>
                    <small className="fw-bold text-dark d-block mb-1">{t('electedWardOfficials')}:</small>
                    <div className="d-flex flex-wrap gap-2">
                      {complaint.representatives.map(r => (
                        <div key={r.id} className="bg-white p-2 rounded border small">
                          <span className="fw-bold text-primary">{r.name}</span> ({r.designation})
                          {r.contact_info && <span className="d-block text-muted extra-small"><i className="bi bi-telephone me-1"></i>{r.contact_info}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="mb-0 small text-muted">{t('noWardOfficials')}</p>
                )}
              </div>
            </div>
          )}

          {/* Public Staff Updates & Comments */}
          <h5 className="fw-bold text-dark border-bottom pb-2 mt-4">
            <i className="bi bi-chat-left-text-fill text-primary me-2"></i> {t('publicStatusUpdates')}
          </h5>
          {complaint.comments && complaint.comments.length > 0 ? (
            <div className="mb-4">
              {complaint.comments.map(c => (
                <div key={c.id} className="p-3 mb-2 rounded border bg-light">
                  <div className="d-flex justify-content-between">
                    <strong className="text-primary">{c.author_name} ({c.author_role})</strong>
                    <small className="text-muted">{new Date(c.created_at).toLocaleString()}</small>
                  </div>
                  <p className="mb-0 mt-1 text-dark">{c.comment_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted small italic mb-4">{t('noPublicUpdates')}</p>
          )}

          {/* Audit History Log */}
          <h5 className="fw-bold text-dark border-bottom pb-2">
            <i className="bi bi-clock-history text-primary me-2"></i> {t('auditTrailLog')}
          </h5>
          {complaint.logs && complaint.logs.length > 0 ? (
            <ul className="list-group list-group-flush mb-4">
              {complaint.logs.map(log => (
                <li key={log.id} className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center bg-transparent">
                  <div>
                    <strong className="text-dark me-2">{log.action}</strong>
                    <span className="text-muted">by {log.performed_by_name}</span>
                  </div>
                  <span className="text-muted small">{new Date(log.timestamp).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted small">{t('noAuditLog')}</p>
          )}

          <div className="mt-4 pt-3 border-top text-center">
            <Link to="/public-complaint" className="btn btn-outline-primary me-2">
              <i className="bi bi-plus-lg me-1"></i> {t('registerNewComplaint')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackComplaint;
